import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { 
  UserIcon, 
  DocumentTextIcon, 
  UserGroupIcon, 
  SparklesIcon,
  EyeIcon,
  PencilIcon,
  PlusIcon,
  ChatBubbleLeftRightIcon,
  HeartIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { useAuthContext } from '../contexts/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { businessService } from '../services/businessService';
import { messageService } from '../services/messageService';
import { userService } from '../services/userService';
import { savedListingsService } from '../services/savedListingsService';
import { uploadFile } from '../services/storageService';
import { sanitizeFileName, isImageFile } from '../utils/imageUtils';
import { PACKAGES } from '../services/stripeService';

const AccountDashboard: React.FC = () => {
  // console.log('AccountDashboard component rendering');
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { currentUser, userProfile } = useAuthContext();
  const [userListings, setUserListings] = useState<any[]>([]);
  const [userConversations, setUserConversations] = useState<any[]>([]);
  const [savedDrafts, setSavedDrafts] = useState<any[]>([]);
  const [filteredLeads, setFilteredLeads] = useState<any[]>([]);
  const [businessTitle, setBusinessTitle] = useState<string>('');
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [showImageUpload, setShowImageUpload] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [cropArea, setCropArea] = useState({ x: 0, y: 0, size: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isUploading, setIsUploading] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Profile editing state
  const [profileForm, setProfileForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    userType: 'buyer' as 'buyer' | 'seller' | 'broker' | 'investor' | 'admin',
    brokerageName: ''
  });
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalListings: 0,
    activeListings: 0,
    totalMessages: 0,
    unreadMessages: 0,
    savedListings: 0,
    totalViews: 0
  });

  usePageTitle('My Account Dashboard | BizBuyMax.com');

  // Check for payment success in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('payment') === 'success') {
      setShowPaymentSuccess(true);
      // Remove the parameter from URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  useEffect(() => {
    const loadDashboardData = async () => {
      if (!currentUser) return;

      try {
        setLoading(true);
        
        // Load user's business listings
        const allUserListings = await businessService.getBusinessesByOwner(currentUser.uid);
        // console.log('All user listings:', allUserListings);
        
        // Separate listings:
        // 1. Active listings (status === 'active')
        // 2. Paid drafts (status === 'draft' && payment.completed === true) - show in "My Business Listings"
        // 3. Unpaid drafts (status === 'draft' && !payment.completed) or pending_payment - show in "Saved Drafts"
        const activeListings = allUserListings.filter((listing: any) => 
          listing.basic?.status === 'active' || 
          (listing.basic?.status === 'draft' && listing.payment?.completed === true)
        );
        
        const draftListings = allUserListings.filter((listing: any) => 
          (listing.basic?.status === 'draft' && !listing.payment?.completed) || 
          listing.basic?.status === 'pending_payment'
        );
        
        // console.log('Active listings:', activeListings);
        // console.log('Draft listings:', draftListings);
        
        setUserListings(activeListings);
        setSavedDrafts(draftListings);

        // Load leads using the new subcollection structure
        // This is more efficient and doesn't require complex indexes
        let conversationsWithInquiries: any[] = [];
        try {
          const leadsByBusiness = await messageService.getLeadsForBusinessOwner(currentUser.uid);
          
          // Transform leads data into conversation-like format for compatibility
          conversationsWithInquiries = leadsByBusiness.flatMap(businessData => 
            businessData.leads.map(lead => ({
              id: lead.conversationId,
              businessId: businessData.businessId,
              businessTitle: businessData.businessTitle,
              participants: [lead.senderId, currentUser.uid],
              status: 'active' as const,
              inquiryData: lead.inquiryData,
              lastMessage: {
                content: lead.inquiryData?.message || '',
                senderId: lead.senderId,
                timestamp: lead.createdAt
              },
              createdAt: lead.createdAt,
              updatedAt: lead.createdAt
            }))
          );
          
          // Sort by most recent
          conversationsWithInquiries.sort((a, b) => {
            const aTime = a.updatedAt?.toMillis?.() || 0;
            const bTime = b.updatedAt?.toMillis?.() || 0;
            return bTime - aTime;
          });
        } catch (leadsError) {
          console.error('Error loading leads from subcollection, falling back to conversations:', leadsError);
          // Fallback to old method if subcollection query fails
          const conversationsResult = await messageService.getConversationsForUser(currentUser.uid);
          conversationsWithInquiries = conversationsResult.map(conv => ({ ...conv, inquiryData: null }));
        }
        
        setUserConversations(conversationsWithInquiries);

        // Load saved listings count
        const savedListingsCount = await savedListingsService.getSavedListingsCount(currentUser.uid);

        // Calculate stats
        const totalViews = allUserListings.reduce((sum, listing) => sum + ((listing as any).analytics?.views || 0), 0);
        
        setStats({
          totalListings: activeListings.length,
          activeListings: activeListings.length,
          totalMessages: conversationsWithInquiries.length,
          unreadMessages: 0, // TODO: Implement unread message tracking
          savedListings: savedListingsCount,
          totalViews
        });

      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [currentUser]);

  // Load filtered leads when businessId query param is present
  useEffect(() => {
    const businessIdFilter = searchParams.get('businessId');
    if (!businessIdFilter || !currentUser) {
      setFilteredLeads([]);
      setBusinessTitle('');
      return;
    }
    
    const loadFilteredLeads = async () => {
      setLoadingLeads(true);
      try {
        // Load leads for this specific business
        const leads = await messageService.getLeadsByBusiness(businessIdFilter);
        
        // Transform to conversation format
        const transformedLeads = leads.map(lead => ({
          id: lead.conversationId,
          businessId: businessIdFilter,
          businessTitle: lead.inquiryData?.businessInfo?.title || '',
          participants: [lead.senderId, currentUser.uid],
          status: 'active' as const,
          inquiryData: lead.inquiryData,
          lastMessage: {
            content: lead.inquiryData?.message || '',
            senderId: lead.senderId,
            timestamp: lead.createdAt
          },
          createdAt: lead.createdAt,
          updatedAt: lead.createdAt
        }));

        setFilteredLeads(transformedLeads);
        
        // Load business title if not in lead data
        if (!transformedLeads[0]?.businessTitle) {
          try {
            const business = await businessService.getBusiness(businessIdFilter);
            setBusinessTitle(business?.basic.title || '');
          } catch (err) {
            console.error('Error loading business title:', err);
            setBusinessTitle('');
          }
        } else {
          setBusinessTitle(transformedLeads[0].businessTitle);
        }
      } catch (error) {
        console.error('Error loading filtered leads:', error);
        setFilteredLeads([]);
        setBusinessTitle('');
      } finally {
        setLoadingLeads(false);
      }
    };

    loadFilteredLeads();
  }, [searchParams, currentUser]);

  // Initialize profile form when userProfile loads
  useEffect(() => {
    if (userProfile && location.pathname === '/account/profile') {
      setProfileForm({
        firstName: userProfile.profile.firstName || '',
        lastName: userProfile.profile.lastName || '',
        phone: userProfile.profile.phone || '',
        userType: (userProfile.profile.userType || 'buyer') as 'buyer' | 'seller' | 'broker' | 'investor' | 'admin',
        brokerageName: (userProfile.profile as any).brokerageName || ''
      });
    }
  }, [userProfile, location.pathname]);

  // Handle profile save
  const handleSaveProfile = async () => {
    if (!currentUser || !userProfile) return;

    try {
      setIsSavingProfile(true);
      await userService.updateUser(currentUser.uid, {
        profile: {
          ...userProfile.profile,
          firstName: profileForm.firstName,
          lastName: profileForm.lastName,
          phone: profileForm.phone,
          userType: profileForm.userType,
          ...(profileForm.userType === 'broker' && { brokerageName: profileForm.brokerageName })
        }
      } as any);
      
      // Reload to show updated profile
      window.location.reload();
    } catch (error: any) {
      console.error('Error saving profile:', error);
      alert('Failed to save profile: ' + (error.message || 'Please try again'));
    } finally {
      setIsSavingProfile(false);
    }
  };

  // Handle image file selection
  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!isImageFile(file)) {
      alert('Please select a valid image file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setSelectedImage(event.target?.result as string);
      setShowImageUpload(true);
    };
    reader.readAsDataURL(file);
  };

  // Initialize crop area when image loads
  const handleImageLoad = () => {
    if (!imageRef.current) return;
    const img = imageRef.current;
    const displayWidth = img.width;
    const displayHeight = img.height;
    const size = Math.min(displayWidth, displayHeight, 300);
    const scaleX = img.naturalWidth / displayWidth;
    const scaleY = img.naturalHeight / displayHeight;
    setCropArea({
      x: ((displayWidth - size) / 2) * scaleX,
      y: ((displayHeight - size) / 2) * scaleY,
      size: size * scaleX
    });
  };

  // Handle crop area dragging
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!imageRef.current || !containerRef.current) return;
    setIsDragging(true);
    const rect = containerRef.current.getBoundingClientRect();
    const img = imageRef.current;
    const scaleX = img.naturalWidth / img.width;
    const scaleY = img.naturalHeight / img.height;
    const mouseX = (e.clientX - rect.left) * scaleX;
    const mouseY = (e.clientY - rect.top) * scaleY;
    setDragStart({
      x: mouseX - cropArea.x,
      y: mouseY - cropArea.y
    });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !imageRef.current || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const img = imageRef.current;
    const scaleX = img.naturalWidth / img.width;
    const scaleY = img.naturalHeight / img.height;
    const newX = (e.clientX - rect.left) * scaleX - dragStart.x;
    const newY = (e.clientY - rect.top) * scaleY - dragStart.y;
    
    const maxX = img.naturalWidth - cropArea.size;
    const maxY = img.naturalHeight - cropArea.size;
    
    setCropArea({
      ...cropArea,
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY))
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Crop and upload image
  const handleCropAndUpload = async () => {
    if (!selectedImage || !imageRef.current || !canvasRef.current || !currentUser) return;

    try {
      setIsUploading(true);
      const img = imageRef.current;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        throw new Error('Could not get canvas context');
      }

      // Set canvas size to square (e.g., 400x400)
      const outputSize = 400;
      canvas.width = outputSize;
      canvas.height = outputSize;

      // Draw cropped image to canvas
      ctx.drawImage(
        img,
        cropArea.x, cropArea.y, cropArea.size, cropArea.size,
        0, 0, outputSize, outputSize
      );

      // Convert canvas to blob
      canvas.toBlob(async (blob) => {
        if (!blob) {
          throw new Error('Failed to create image blob');
        }

        // Create File from blob with sanitized filename
        const sanitizedFileName = sanitizeFileName(`profile_${currentUser.uid}.png`);
        const file = new File([blob], sanitizedFileName, { type: 'image/png' });

        // Upload to Firebase Storage
        const storagePath = `profiles/${currentUser.uid}/`;
        const downloadURL = await uploadFile(file, storagePath, true);

        // Update user profile
        if (userProfile) {
          await userService.updateUser(currentUser.uid, {
            profile: {
              ...userProfile.profile,
              avatar: downloadURL
            }
          } as any);
        }

        // Close modal and reset
        setShowImageUpload(false);
        setSelectedImage(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }

        // Reload page to show new avatar
        window.location.reload();
      }, 'image/png', 0.9);
    } catch (error: any) {
      console.error('Error uploading profile image:', error);
      alert('Failed to upload profile image: ' + (error.message || 'Please try again'));
    } finally {
      setIsUploading(false);
    }
  };

  // Helper function to get next upgrade level and price
  const getNextUpgrade = (currentPackageId: string | undefined) => {
    if (!currentPackageId) {
      return { name: '3-Months', price: 199 };
    }
    const packageOrder = ['3months', '6months', '12months'];
    const currentIndex = packageOrder.indexOf(currentPackageId);
    if (currentIndex === -1 || currentIndex === packageOrder.length - 1) {
      return null; // No upgrade available
    }
    const nextPackageId = packageOrder[currentIndex + 1];
    const nextPackage = PACKAGES[nextPackageId];
    return {
      name: nextPackage.name,
      price: nextPackage.priceCAD
    };
  };

  // Helper function to truncate text
  const truncateText = (text: string, maxLength: number) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  // Helper function to format expiration date
  const formatExpirationDate = (expiresAt: any) => {
    if (!expiresAt) return 'N/A';
    try {
      const date = expiresAt.toDate ? expiresAt.toDate() : new Date(expiresAt);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return 'N/A';
    }
  };

  // Helper function to get status display text
  const getStatusText = (listing: any) => {
    if (listing.basic.status === 'draft' && listing.payment?.completed) {
      return 'Paid - Finish Listing';
    }
    return listing.basic.status || 'draft';
  };

  // Render listing item
  const renderListingItem = (listing: any, isDraft: boolean = false) => {
    const featuredImage = listing.media?.featuredImage || listing.media?.images?.[0];
    const packageId = (listing as any).package?.id;
    const nextUpgrade = getNextUpgrade(packageId);
    const mlsNumber = (listing as any).mlsNumber || (listing as any).realtor?.mlsNumber;
    const listingName = listing.basic.title || 'Untitled Listing';
    const statusText = getStatusText(listing);

    return (
      <div key={listing.id} className="flex items-start py-4 border-b border-gray-200 last:border-b-0">
        {/* Image - Linked to edit page */}
        <Link
          to={`/account/listings/edit/${listing.id}`}
          className="w-24 h-24 flex-shrink-0 mr-4"
        >
          {featuredImage ? (
            <img 
              src={featuredImage} 
              alt={listingName}
              className="w-full h-full object-cover hover:opacity-90 transition-opacity"
            />
          ) : (
            <div className="w-full h-full bg-gray-200 flex items-center justify-center hover:bg-gray-300 transition-colors">
              <span className="text-xs text-gray-500 font-medium">No Image</span>
            </div>
          )}
        </Link>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="space-y-1">
            {/* Line 1: Title - Linked to edit page */}
            <Link
              to={`/account/listings/edit/${listing.id}`}
              className="text-lg font-bold text-primary-600 hover:text-primary-700 transition-colors block"
            >
              {truncateText(listingName, 40)}
            </Link>
            
            {/* Line 2: Listing name, ID, Status */}
            <p className="text-base text-gray-600">
              {listingName}: {listing.id} - {statusText}
            </p>
            
            {/* Line 3: MLS ID */}
            <p className="text-base text-gray-600">
              MLS ID: {mlsNumber || 'N/A'}
            </p>
            
            {/* Line 4: Expiration date */}
            <p className="text-base text-gray-600">
              Expiration date: {formatExpirationDate(listing.basic.expiresAt)}
            </p>
            
            {/* Upgrade message */}
            {nextUpgrade && listing.basic.status === 'active' && (
              <p className="text-base text-gray-600 mt-1">
                <Link
                  to={`/account/upgrades?listing=${listing.id}&upgrade=${nextUpgrade.name.toLowerCase().replace(/\s+/g, '_')}`}
                  className="text-primary-600 hover:text-primary-700 font-medium"
                >
                  Upgrade to {nextUpgrade.name} for ${nextUpgrade.price}
                </Link>
                {' '}and have your listing sent to thousands of actual investors, buyers and business professionals.
              </p>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2 ml-4 flex-shrink-0">
          {listing.basic.status === 'draft' && listing.payment?.completed ? (
            <Link
              to={`/sell-business?draft=${listing.id}`}
              className="px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 transition-colors text-sm font-medium text-center"
            >
              Finish Listing
            </Link>
          ) : (
            <>
              <Link
                to={listing.basic.seoUrl ? `/business/${listing.basic.seoUrl}` : `/business/${listing.id}`}
                className="px-4 py-2 bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors text-sm font-medium flex items-center justify-center gap-2"
              >
                <EyeIcon className="h-4 w-4" />
                View Listing
              </Link>
              <Link
                to={`/account/leads?businessId=${listing.id}`}
                className="px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 transition-colors text-sm font-medium flex items-center justify-center gap-2"
              >
                <UserGroupIcon className="h-4 w-4" />
                View Leads
              </Link>
              <Link
                to={`/account/listings/edit/${listing.id}`}
                className="px-4 py-2 bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors text-sm font-medium flex items-center justify-center gap-2"
              >
                <PencilIcon className="h-4 w-4" />
                Edit Listing
              </Link>
            </>
          )}
        </div>
      </div>
    );
  };

  // Render Sidebar
  const renderSidebar = () => (
    <div className="lg:col-span-3 space-y-6">
      {/* Quick Actions */}
      <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 rounded-2xl shadow-xl border border-gray-200/50 p-6 backdrop-blur-sm">
        <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
          <div className="w-2 h-8 bg-gradient-to-b from-primary-500 to-secondary-500 rounded-full mr-3"></div>
          Quick Actions
        </h3>
        <div className="space-y-3">
          <Link
            to="/sell-business"
            className="flex items-center p-4 text-gray-700 hover:text-primary-700 bg-white hover:bg-gradient-to-r hover:from-primary-50 hover:to-secondary-50 rounded-xl transition-all duration-300 shadow-sm hover:shadow-md border border-gray-100 hover:border-primary-200"
          >
            <div className="p-2 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg mr-4 shadow-sm">
              <PlusIcon className="h-5 w-5 text-white" />
            </div>
            <span className="font-medium">List New Business</span>
          </Link>
          <Link
            to="/account/listings"
            className="flex items-center p-4 text-gray-700 hover:text-blue-700 bg-white hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 rounded-xl transition-all duration-300 shadow-sm hover:shadow-md border border-gray-100 hover:border-blue-200"
          >
            <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg mr-4 shadow-sm">
              <DocumentTextIcon className="h-5 w-5 text-white" />
            </div>
            <span className="font-medium">Manage Listings</span>
          </Link>
          <Link
            to="/account/leads"
            className="flex items-center p-4 text-gray-700 hover:text-green-700 bg-white hover:bg-gradient-to-r hover:from-green-50 hover:to-emerald-50 rounded-xl transition-all duration-300 shadow-sm hover:shadow-md border border-gray-100 hover:border-green-200"
          >
            <div className="p-2 bg-gradient-to-br from-green-500 to-green-600 rounded-lg mr-4 shadow-sm">
              <UserGroupIcon className="h-5 w-5 text-white" />
            </div>
            <span className="font-medium">View Leads</span>
          </Link>
          <Link
            to="/account/saved-listings"
            className="flex items-center p-4 text-gray-700 hover:text-red-700 bg-white hover:bg-gradient-to-r hover:from-red-50 hover:to-pink-50 rounded-xl transition-all duration-300 shadow-sm hover:shadow-md border border-gray-100 hover:border-red-200"
          >
            <div className="p-2 bg-gradient-to-br from-red-500 to-red-600 rounded-lg mr-4 shadow-sm">
              <HeartIcon className="h-5 w-5 text-white" />
            </div>
            <span className="font-medium">Saved Listings</span>
          </Link>
          <Link
            to="/account/upgrades"
            className="flex items-center p-4 text-gray-700 hover:text-purple-700 bg-white hover:bg-gradient-to-r hover:from-purple-50 hover:to-violet-50 rounded-xl transition-all duration-300 shadow-sm hover:shadow-md border border-gray-100 hover:border-purple-200"
          >
            <div className="p-2 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg mr-4 shadow-sm">
              <SparklesIcon className="h-5 w-5 text-white" />
            </div>
            <span className="font-medium">Upgrade Account</span>
          </Link>
          <Link
            to="/account/profile"
            className="flex items-center p-4 text-gray-700 hover:text-gray-800 bg-white hover:bg-gradient-to-r hover:from-gray-50 hover:to-slate-50 rounded-xl transition-all duration-300 shadow-sm hover:shadow-md border border-gray-100 hover:border-gray-200"
          >
            <div className="p-2 bg-gradient-to-br from-gray-500 to-gray-600 rounded-lg mr-4 shadow-sm">
              <UserIcon className="h-5 w-5 text-white" />
            </div>
            <span className="font-medium">Edit Profile</span>
          </Link>
        </div>
      </div>

      {/* Account Stats */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Statistics</h3>
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Total Listings</span>
            <span className="font-medium">{stats.totalListings}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Active Listings</span>
            <span className="font-medium text-green-600">{stats.activeListings}</span>
          </div>
          {/* <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Total Views</span>
            <span className="font-medium">{stats.totalViews.toLocaleString()}</span>
          </div> */}
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Leads</span>
            <span className="font-medium">{stats.totalMessages}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Saved Listings</span>
            <span className="font-medium text-primary-600">{stats.savedListings}</span>
          </div>
          {stats.unreadMessages > 0 && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Unread Leads</span>
              <span className="font-medium text-red-600">{stats.unreadMessages}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Helper function to format date
  const formatLeadDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'N/A';
    }
  };

  // Render Leads View
  const renderLeadsView = () => {
    const businessIdFilter = searchParams.get('businessId');
    const displayLeads = businessIdFilter ? filteredLeads : userConversations;
    const displayTitle = businessIdFilter 
      ? `${businessTitle || 'Business'}'s Leads (${filteredLeads.length})`
      : `Leads (${userConversations.length})`;

    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-9">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">{displayTitle}</h2>
                  {businessIdFilter && (
                    <Link
                      to="/account/leads"
                      className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                    >
                      View All Leads
                    </Link>
                  )}
                </div>
              </div>
              <div className="p-6">
                {(businessIdFilter && loadingLeads) ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                    <p className="mt-2 text-gray-600">Loading leads...</p>
                  </div>
                ) : displayLeads.length > 0 ? (
                  <div className="space-y-4">
                    {displayLeads.map((conversation: any) => {
                    const inquiryData = conversation.inquiryData;
                    const businessInfo = inquiryData?.businessInfo;
                    const leadDate = conversation.createdAt || conversation.updatedAt;
                    return (
                      <div key={conversation.id} className="border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                        <div className="p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-2">
                                <h3 className="font-semibold text-gray-900 text-lg">
                                  {inquiryData?.name || 'Unknown Lead'}
                                </h3>
                                {leadDate && (
                                  <span className="text-xs text-gray-500">
                                    {formatLeadDate(leadDate)}
                                  </span>
                                )}
                              </div>
                              {inquiryData && (
                                <div className="space-y-1 text-sm">
                                  <div className="flex items-center">
                                    <span className="text-gray-600 font-medium w-20">Email:</span>
                                    <span className="text-gray-900">{inquiryData.email}</span>
                                    {!businessIdFilter && inquiryData.username && inquiryData.username !== inquiryData.email && (
                                      <span className="ml-2 text-gray-500">({inquiryData.username})</span>
                                    )}
                                  </div>
                                  <div className="flex items-center">
                                    <span className="text-gray-600 font-medium w-20">Phone:</span>
                                    <span className="text-gray-900">{inquiryData.phone || 'N/A'}</span>
                                  </div>
                                  {!businessIdFilter && businessInfo && (
                                    <div className="mt-2 pt-2 border-t border-gray-200">
                                      <div className="flex items-start gap-3">
                                        {businessInfo.firstImageUrl && (
                                          <img 
                                            src={businessInfo.firstImageUrl} 
                                            alt={businessInfo.title}
                                            className="w-16 h-16 object-cover rounded-md flex-shrink-0"
                                            onError={(e) => {
                                              const target = e.target as HTMLImageElement;
                                              target.style.display = 'none';
                                            }}
                                          />
                                        )}
                                        <div className="flex-1 min-w-0">
                                          <div className="font-medium text-gray-900 mb-1">{businessInfo.title}</div>
                                          <div className="text-gray-600 text-xs mb-1">
                                            Asking Price: ${businessInfo.askingPrice?.toLocaleString() || 'N/A'}
                                          </div>
                                          <Link 
                                            to={businessInfo.url}
                                            className="text-primary-600 hover:text-primary-700 text-xs mt-1 inline-block"
                                          >
                                            View Listing →
                                          </Link>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                  {inquiryData.message && (
                                    <div className="mt-2 pt-2 border-t border-gray-200">
                                      <span className="text-gray-600 font-medium block mb-1">Message:</span>
                                      <p className="text-gray-900">{inquiryData.message}</p>
                                    </div>
                                  )}
                                </div>
                              )}
                              {!inquiryData && (
                                <p className="text-sm text-gray-600 mt-1">
                                  {conversation.lastMessage?.content || 'No message content'}
                                </p>
                              )}
                            </div>
                            {!businessIdFilter && (
                              <Link
                                to={`/account/leads/${conversation.id}`}
                                className="ml-4 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors text-sm font-medium whitespace-nowrap"
                              >
                                View Details
                              </Link>
                            )}
                          </div>
                          {!businessIdFilter && (
                            <div className="flex items-center space-x-4 mt-3 pt-3 border-t border-gray-200">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                conversation.status === 'active' 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {conversation.status}
                              </span>
                              <span className="text-xs text-gray-500">
                                {conversation.businessTitle || businessInfo?.title ? (
                                  <span>Business: {conversation.businessTitle || businessInfo?.title}</span>
                                ) : (
                                  <span>Business ID: {conversation.businessId}</span>
                                )}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <ChatBubbleLeftRightIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {businessIdFilter ? 'No leads for this business yet' : 'No leads yet'}
                  </h3>
                  <p className="text-gray-600">
                    {businessIdFilter 
                      ? 'Leads from potential buyers will appear here when they contact you about this business.'
                      : 'Leads from potential buyers will appear here'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
        {renderSidebar()}
      </div>
    </div>
    );
  };

  // Render Listings View
  const renderListingsView = () => (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-8">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-9 space-y-8">
          {/* My Business Listings */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">My Business Listings ({userListings.length})</h2>
            </div>
            <div className="p-6">
              {userListings.length > 0 ? (
                <div>
                  {userListings.map((listing) => renderListingItem(listing, false))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No listings yet</h3>
                  <p className="text-gray-600 mb-4">Start by listing your first business for sale</p>
                  <Link
                    to="/sell-business"
                    className="bg-primary-600 text-white px-4 py-2 rounded-md font-medium hover:bg-primary-700 transition-colors"
                  >
                    List Your Business
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Saved Drafts */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Saved Drafts ({savedDrafts.length})</h2>
            </div>
            <div className="p-6">
              {savedDrafts.length > 0 ? (
                <div>
                  {savedDrafts.map((draft) => renderListingItem(draft, true))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No saved drafts</h3>
                  <p className="text-gray-600 mb-4">Start creating a listing to save drafts</p>
                  <Link
                    to="/sell-business"
                    className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                  >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Create New Listing
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
        {renderSidebar()}
      </div>
    </div>
  );

  // Render Profile View
  const renderProfileView = () => {
    if (!userProfile) return null;
    
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-9">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Edit Profile</h2>
              </div>
              <div className="p-6 space-y-6">
                {/* Profile Image */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Profile Image</label>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="relative w-32 h-32 bg-gray-100 rounded-lg flex items-center justify-center border-2 border-gray-300 hover:border-primary-500 transition-colors cursor-pointer group"
                  >
                    {userProfile.profile.avatar ? (
                      <img 
                        src={userProfile.profile.avatar} 
                        alt="Profile" 
                        className="w-full h-full rounded-lg object-cover"
                      />
                    ) : (
                      <UserIcon className="h-16 w-16 text-gray-400" />
                    )}
                    <div className="absolute inset-0 bg-black/20 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <PencilIcon className="h-6 w-6 text-white" />
                    </div>
                  </button>
                  <p className="mt-2 text-sm text-gray-500">Click to upload a new profile image</p>
                </div>

                {/* First Name */}
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                    First Name
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    value={profileForm.firstName}
                    onChange={(e) => setProfileForm({ ...profileForm, firstName: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                {/* Last Name */}
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    value={profileForm.lastName}
                    onChange={(e) => setProfileForm({ ...profileForm, lastName: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                {/* Phone Number */}
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    value={profileForm.phone}
                    onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                {/* User Type */}
                <div>
                  <label htmlFor="userType" className="block text-sm font-medium text-gray-700 mb-2">
                    Type of User
                  </label>
                  <select
                    id="userType"
                    value={profileForm.userType}
                    onChange={(e) => setProfileForm({ ...profileForm, userType: e.target.value as any })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="buyer">Buyer</option>
                    <option value="seller">Seller</option>
                    <option value="broker">Broker</option>
                    <option value="investor">Investor</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>

                {/* Brokerage Name (only if Broker) */}
                {profileForm.userType === 'broker' && (
                  <div>
                    <label htmlFor="brokerageName" className="block text-sm font-medium text-gray-700 mb-2">
                      Brokerage Name <span className="text-gray-500 text-xs">(optional)</span>
                    </label>
                    <input
                      type="text"
                      id="brokerageName"
                      value={profileForm.brokerageName}
                      onChange={(e) => setProfileForm({ ...profileForm, brokerageName: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Enter brokerage name"
                    />
                  </div>
                )}

                {/* Save Button */}
                <div className="flex justify-end pt-4 border-t border-gray-200">
                  <button
                    onClick={handleSaveProfile}
                    disabled={isSavingProfile}
                    className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {isSavingProfile ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Saving...
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
          {renderSidebar()}
        </div>
      </div>
    );
  };

  if (!currentUser || !userProfile) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-lg mb-4">⚠️ Please sign in to access your dashboard</div>
          <Link to="/signin" className="bg-primary-600 text-white px-4 py-2 rounded hover:bg-primary-700">
            Sign In
          </Link>
        </div>
      </div>
    );
  }

  // Determine which view to render based on route
  const currentPath = location.pathname;
  let pageTitle = 'My Account Dashboard';
  let headerTitle = 'My Account Dashboard';

  if (currentPath === '/account/leads') {
    pageTitle = 'My Leads | BizBuyMax.com';
    headerTitle = 'My Leads';
  } else if (currentPath === '/account/listings') {
    pageTitle = 'My Listings | BizBuyMax.com';
    headerTitle = 'My Listings';
  } else if (currentPath === '/account/profile') {
    pageTitle = 'Edit Profile | BizBuyMax.com';
    headerTitle = 'Edit Profile';
  }

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content="Manage your business listings, leads, and account settings" />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Payment Success Message */}
        {showPaymentSuccess && (
          <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-green-700">
                  <strong>Payment Successful!</strong> Your business listing has been activated and is now live on our marketplace.
                </p>
              </div>
              <div className="ml-auto pl-3">
                <div className="-mx-1.5 -my-1.5">
                  <button
                    onClick={() => setShowPaymentSuccess(false)}
                    className="inline-flex bg-green-50 rounded-md p-1.5 text-green-500 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-green-50 focus:ring-green-600"
                  >
                    <span className="sr-only">Dismiss</span>
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="bg-gradient-to-r from-primary-600 via-primary-700 to-secondary-600 shadow-lg">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="relative w-24 h-24 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center border border-white/30 shadow-lg hover:bg-white/30 transition-all cursor-pointer group"
                  title="Upload profile image"
                >
                  {userProfile?.profile.avatar ? (
                    <img 
                      src={userProfile?.profile.avatar} 
                      alt="Profile" 
                      className="w-full h-full rounded-lg object-cover"
                    />
                  ) : (
                    <UserIcon className="h-12 w-12 text-white" />
                  )}
                  <div className="absolute inset-0 bg-black/20 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <PencilIcon className="h-5 w-5 text-white" />
                  </div>
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="hidden"
                />
                <div>
                  <h1 className="text-3xl font-bold text-white drop-shadow-sm">{headerTitle}</h1>
                  {currentPath === '/account/dashboard' && userProfile && (
                    <p className="text-primary-100 mt-1 text-lg">Welcome back, {userProfile?.profile.firstName}!</p>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <Link
                  to="/sell-business"
                  className="bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 flex items-center space-x-2 border border-white/30 shadow-lg hover:shadow-xl"
                >
                  <PlusIcon className="h-5 w-5" />
                  <span>List Business</span>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading...</p>
          </div>
        ) : (
          <>
            {currentPath === '/account/leads' && renderLeadsView()}
            {currentPath === '/account/listings' && renderListingsView()}
            {currentPath === '/account/profile' && renderProfileView()}
            {currentPath === '/account/dashboard' && (
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-8">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                  {/* Main Content */}
                  <div className="lg:col-span-9 space-y-8">
                    {/* Recent Listings */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                      <div className="px-6 py-4 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                          <h2 className="text-lg font-semibold text-gray-900">My Business Listings ({stats.totalListings})</h2>
                          <Link
                            to="/account/listings"
                            className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                          >
                            View All
                          </Link>
                        </div>
                      </div>
                      <div className="p-6">
                        {userListings.length > 0 ? (
                          <div>
                            {userListings.slice(0, 3).map((listing) => renderListingItem(listing, false))}
                          </div>
                        ) : (
                          <div className="text-center py-8">
                            <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No listings yet</h3>
                            <p className="text-gray-600 mb-4">Start by listing your first business for sale</p>
                            <Link
                              to="/sell-business"
                              className="bg-primary-600 text-white px-4 py-2 rounded-md font-medium hover:bg-primary-700 transition-colors"
                            >
                              List Your Business
                            </Link>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Recent Messages */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                      <div className="px-6 py-4 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                          <h2 className="text-lg font-semibold text-gray-900">Recent Leads ({stats.totalMessages})</h2>
                          <Link
                            to="/account/leads"
                            className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                          >
                            View All
                          </Link>
                        </div>
                      </div>
                      <div className="p-6">
                        {userConversations.length > 0 ? (
                          <div className="space-y-4">
                            {userConversations.slice(0, 3).map((conversation: any) => {
                              const inquiryData = conversation.inquiryData;
                              const businessInfo = inquiryData?.businessInfo;
                              return (
                                <div key={conversation.id} className="border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                                  <div className="p-4">
                                    <div className="flex items-start justify-between mb-2">
                                      <div className="flex-1">
                                        <h3 className="font-semibold text-gray-900">
                                          {inquiryData?.name || 'Unknown Lead'}
                                        </h3>
                                        {inquiryData && (
                                          <div className="mt-2 space-y-1 text-sm">
                                            <div className="flex items-center">
                                              <span className="text-gray-600 font-medium w-16">Email:</span>
                                              <span className="text-gray-900">{inquiryData.email}</span>
                                              {inquiryData.username && inquiryData.username !== inquiryData.email && (
                                                <span className="ml-2 text-gray-500 text-xs">({inquiryData.username})</span>
                                              )}
                                            </div>
                                            <div className="flex items-center">
                                              <span className="text-gray-600 font-medium w-16">Phone:</span>
                                              <span className="text-gray-900">{inquiryData.phone || 'N/A'}</span>
                                            </div>
                                            {businessInfo && (
                                              <div className="mt-2 pt-2 border-t border-gray-200">
                                                <div className="flex items-start gap-2">
                                                  {businessInfo.firstImageUrl && (
                                                    <img 
                                                      src={businessInfo.firstImageUrl} 
                                                      alt={businessInfo.title}
                                                      className="w-12 h-12 object-cover rounded-md flex-shrink-0"
                                                      onError={(e) => {
                                                        const target = e.target as HTMLImageElement;
                                                        target.style.display = 'none';
                                                      }}
                                                    />
                                                  )}
                                                  <div className="flex-1 min-w-0">
                                                    <div className="font-medium text-gray-900 text-xs mb-1">{businessInfo.title}</div>
                                                    <div className="text-gray-600 text-xs mb-1">
                                                      Asking Price: ${businessInfo.askingPrice?.toLocaleString() || 'N/A'}
                                                    </div>
                                                    <Link 
                                                      to={businessInfo.url}
                                                      className="text-primary-600 hover:text-primary-700 text-xs inline-block"
                                                    >
                                                      View Listing →
                                                    </Link>
                                                  </div>
                                                </div>
                                              </div>
                                            )}
                                            {inquiryData.message && (
                                              <div className="mt-1 pt-1 border-t border-gray-200">
                                                <p className="text-gray-700 text-xs line-clamp-2">{inquiryData.message}</p>
                                              </div>
                                            )}
                                          </div>
                                        )}
                                        {!inquiryData && (
                                          <p className="text-sm text-gray-600 mt-1">
                                            {conversation.lastMessage?.content || 'No message content'}
                                          </p>
                                        )}
                                      </div>
                                      <Link
                                        to={`/account/leads/${conversation.id}`}
                                        className="ml-4 px-3 py-1 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors text-xs font-medium whitespace-nowrap"
                                      >
                                        View
                                      </Link>
                                    </div>
                                    <div className="flex items-center space-x-3 mt-2 pt-2 border-t border-gray-200">
                                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                        conversation.status === 'active' 
                                          ? 'bg-green-100 text-green-800' 
                                          : 'bg-gray-100 text-gray-800'
                                      }`}>
                                        {conversation.status}
                                      </span>
                                      {(conversation.businessTitle || businessInfo?.title) && (
                                        <span className="text-xs text-gray-500">
                                          {conversation.businessTitle || businessInfo?.title}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="text-center py-8">
                            <ChatBubbleLeftRightIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No leads yet</h3>
                            <p className="text-gray-600">Leads from potential buyers will appear here</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Saved Drafts */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                      <div className="px-6 py-4 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                          <h2 className="text-lg font-semibold text-gray-900">Saved Drafts ({savedDrafts.length})</h2>
                          <Link
                            to="/sell-business"
                            className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                          >
                            Create New
                          </Link>
                        </div>
                      </div>
                      <div className="p-6">
                        {savedDrafts.length > 0 ? (
                          <div>
                            {savedDrafts.slice(0, 3).map((draft) => renderListingItem(draft, true))}
                            {savedDrafts.length > 3 && (
                              <div className="text-center pt-4 border-t border-gray-200 mt-4">
                                <Link
                                  to="/account/drafts"
                                  className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                                >
                                  View all {savedDrafts.length} drafts
                                </Link>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="text-center py-8">
                            <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No saved drafts</h3>
                            <p className="text-gray-600 mb-4">Start creating a listing to save drafts</p>
                            <Link
                              to="/sell-business"
                              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                            >
                              <PlusIcon className="h-4 w-4 mr-2" />
                              Create New Listing
                            </Link>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Sidebar */}
                  <div className="lg:col-span-3 space-y-6">
                    {/* Quick Actions */}
                    <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 rounded-2xl shadow-xl border border-gray-200/50 p-6 backdrop-blur-sm">
                      <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                        <div className="w-2 h-8 bg-gradient-to-b from-primary-500 to-secondary-500 rounded-full mr-3"></div>
                        Quick Actions
                      </h3>
                      <div className="space-y-3">
                        <Link
                          to="/sell-business"
                          className="flex items-center p-4 text-gray-700 hover:text-primary-700 bg-white hover:bg-gradient-to-r hover:from-primary-50 hover:to-secondary-50 rounded-xl transition-all duration-300 shadow-sm hover:shadow-md border border-gray-100 hover:border-primary-200"
                        >
                          <div className="p-2 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg mr-4 shadow-sm">
                            <PlusIcon className="h-5 w-5 text-white" />
                          </div>
                          <span className="font-medium">List New Business</span>
                        </Link>
                        <Link
                          to="/account/listings"
                          className="flex items-center p-4 text-gray-700 hover:text-blue-700 bg-white hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 rounded-xl transition-all duration-300 shadow-sm hover:shadow-md border border-gray-100 hover:border-blue-200"
                        >
                          <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg mr-4 shadow-sm">
                            <DocumentTextIcon className="h-5 w-5 text-white" />
                          </div>
                          <span className="font-medium">Manage Listings</span>
                        </Link>
                        <Link
                          to="/account/leads"
                          className="flex items-center p-4 text-gray-700 hover:text-green-700 bg-white hover:bg-gradient-to-r hover:from-green-50 hover:to-emerald-50 rounded-xl transition-all duration-300 shadow-sm hover:shadow-md border border-gray-100 hover:border-green-200"
                        >
                          <div className="p-2 bg-gradient-to-br from-green-500 to-green-600 rounded-lg mr-4 shadow-sm">
                            <UserGroupIcon className="h-5 w-5 text-white" />
                          </div>
                          <span className="font-medium">View Leads</span>
                        </Link>
                        <Link
                          to="/account/saved-listings"
                          className="flex items-center p-4 text-gray-700 hover:text-red-700 bg-white hover:bg-gradient-to-r hover:from-red-50 hover:to-pink-50 rounded-xl transition-all duration-300 shadow-sm hover:shadow-md border border-gray-100 hover:border-red-200"
                        >
                          <div className="p-2 bg-gradient-to-br from-red-500 to-red-600 rounded-lg mr-4 shadow-sm">
                            <HeartIcon className="h-5 w-5 text-white" />
                          </div>
                          <span className="font-medium">Saved Listings</span>
                        </Link>
                        <Link
                          to="/account/upgrades"
                          className="flex items-center p-4 text-gray-700 hover:text-purple-700 bg-white hover:bg-gradient-to-r hover:from-purple-50 hover:to-violet-50 rounded-xl transition-all duration-300 shadow-sm hover:shadow-md border border-gray-100 hover:border-purple-200"
                        >
                          <div className="p-2 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg mr-4 shadow-sm">
                            <SparklesIcon className="h-5 w-5 text-white" />
                          </div>
                          <span className="font-medium">Upgrade Account</span>
                        </Link>
                        <Link
                          to="/account/profile"
                          className="flex items-center p-4 text-gray-700 hover:text-gray-800 bg-white hover:bg-gradient-to-r hover:from-gray-50 hover:to-slate-50 rounded-xl transition-all duration-300 shadow-sm hover:shadow-md border border-gray-100 hover:border-gray-200"
                        >
                          <div className="p-2 bg-gradient-to-br from-gray-500 to-gray-600 rounded-lg mr-4 shadow-sm">
                            <UserIcon className="h-5 w-5 text-white" />
                          </div>
                          <span className="font-medium">Edit Profile</span>
                        </Link>
                      </div>
                    </div>

                    {/* Account Stats */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Statistics</h3>
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Total Listings</span>
                          <span className="font-medium">{stats.totalListings}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Active Listings</span>
                          <span className="font-medium text-green-600">{stats.activeListings}</span>
                        </div>
                        {/* <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Total Views</span>
                          <span className="font-medium">{stats.totalViews.toLocaleString()}</span>
                        </div> */}
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Leads</span>
                          <span className="font-medium">{stats.totalMessages}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Saved Listings</span>
                          <span className="font-medium text-primary-600">{stats.savedListings}</span>
                        </div>
                        {stats.unreadMessages > 0 && (
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Unread Leads</span>
                            <span className="font-medium text-red-600">{stats.unreadMessages}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Image Upload/Crop Modal */}
      {showImageUpload && selectedImage && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Crop Profile Image</h2>
                <button
                  onClick={() => {
                    setShowImageUpload(false);
                    setSelectedImage(null);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  Drag the square to select the area you want to use for your profile image.
                </p>
                <div 
                  ref={containerRef}
                  className="relative border-2 border-gray-300 rounded-lg overflow-hidden bg-gray-100"
                  style={{ maxWidth: '500px', maxHeight: '500px', margin: '0 auto' }}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                >
                  <img
                    ref={imageRef}
                    src={selectedImage || undefined}
                    alt="Preview"
                    className="max-w-full max-h-[500px] block"
                    style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
                    onLoad={handleImageLoad}
                  />
                  {/* Crop overlay */}
                  <div
                    className="absolute border-2 border-blue-500 bg-blue-500/20"
                    style={{
                      left: `${(cropArea.x / (imageRef.current?.naturalWidth || 1)) * 100}%`,
                      top: `${(cropArea.y / (imageRef.current?.naturalHeight || 1)) * 100}%`,
                      width: `${(cropArea.size / (imageRef.current?.naturalWidth || 1)) * 100}%`,
                      height: `${(cropArea.size / (imageRef.current?.naturalHeight || 1)) * 100}%`,
                      cursor: isDragging ? 'grabbing' : 'grab'
                    }}
                  >
                    <div className="absolute inset-0 border-4 border-white"></div>
                  </div>
                </div>
              </div>

              <canvas ref={canvasRef} className="hidden" />

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowImageUpload(false);
                    setSelectedImage(null);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  disabled={isUploading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCropAndUpload}
                  disabled={isUploading}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isUploading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Uploading...
                    </>
                  ) : (
                    'Upload'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AccountDashboard; 